/**
 * Procedural layer color generator — golden angle hue + Halton lightness/chroma in OKLCH.
 *
 * Produces perceptually uniform, maximally-spread colors that scale to any count
 * without manual palette maintenance. Returns hex strings for downstream compatibility.
 *
 * Theme adaptation: the normalized seeds (h, l, c) ∈ [0, 1] are theme-invariant —
 * only the L/C range mapping changes. Hue is absolute and preserved across themes,
 * so every palette color keeps its identity (green stays green, cyan stays cyan) —
 * dark mode is neon phosphor, light mode is wall-street ink. Relative perceptual
 * distance between colors is preserved because the seeds don't change: color 5
 * stays "between" 2 and 8 regardless of theme.
 *
 * Axes:
 *   Hue       — golden angle (φ-based irrational rotation), optimal circle-filling
 *   Lightness — Halton base 2, fastest line-segment gap-filling
 *   Chroma    — Halton base 3, coprime with base 2 for independent coverage
 */

export type PaletteTheme = "dark" | "light";
export const CUSTOM_LAYER_COLOR = "__abg_custom_rainbow__";
export const CUSTOM_LAYER_SOLID_COLOR = "#21f7d8";
export const CUSTOM_LAYER_GRADIENT =
  "linear-gradient(90deg, #21f7d8, #4aa8ff, #9b7cff, #ff56c7, #ffb84d, #42f56f)";
export const CUSTOM_LAYER_BORDER_GRADIENT =
  "linear-gradient(90deg, rgba(33,247,216,0.3), rgba(74,168,255,0.3), rgba(155,124,255,0.3), rgba(255,86,199,0.3), rgba(255,184,77,0.3), rgba(66,245,111,0.3))";

export function isCustomLayerColor(color: string | null | undefined): boolean {
  return color === CUSTOM_LAYER_COLOR;
}

// ── Halton sequence ────────────────────────────────────────────────────────

/** Halton low-discrepancy sequence value for index i in given prime base. */
function halton(i: number, base: number): number {
  let result = 0;
  let f = 1;
  let n = i + 1; // 1-indexed so index 0 produces a nonzero value
  while (n > 0) {
    f /= base;
    result += (n % base) * f;
    n = Math.floor(n / base);
  }
  return result;
}

// ── OKLCH ↔ sRGB conversion ───────────────────────────────────────────────

/** Convert OKLCH (L 0–1, C 0–0.4, H 0–360) → linear sRGB [r, g, b] each 0–1. */
function oklchToLinearRGB(
  L: number,
  C: number,
  H: number,
): [number, number, number] {
  // OKLCH → OKLAB
  const hRad = (H * Math.PI) / 180;
  const a = C * Math.cos(hRad);
  const b = C * Math.sin(hRad);

  // OKLAB → LMS (cube root space)
  const l_ = L + 0.3963377774 * a + 0.2158037573 * b;
  const m_ = L - 0.1055613458 * a - 0.0638541728 * b;
  const s_ = L - 0.0894841775 * a - 1.291485548 * b;

  // LMS cube roots → linear LMS
  const l = l_ * l_ * l_;
  const m = m_ * m_ * m_;
  const s = s_ * s_ * s_;

  // Linear LMS → linear sRGB
  const r = +4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s;
  const g = -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s;
  const bOut = -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s;

  return [r, g, bOut];
}

/** Convert linear sRGB (each 0–1) → OKLCH (L 0–1, C 0–0.4, H 0–360). */
function linearRGBToOklch(
  r: number,
  g: number,
  b: number,
): [number, number, number] {
  // Linear sRGB → linear LMS (Björn Ottosson's inverse matrix)
  const l = 0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b;
  const m = 0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b;
  const s = 0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b;

  // Linear LMS → LMS cube root (sign-preserving for out-of-gamut safety)
  const l_ = Math.cbrt(l);
  const m_ = Math.cbrt(m);
  const s_ = Math.cbrt(s);

  // LMS cube root → OKLAB
  const L = 0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_;
  const a = 1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_;
  const bLab = 0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_;

  // OKLAB → OKLCH
  const C = Math.sqrt(a * a + bLab * bLab);
  let H = (Math.atan2(bLab, a) * 180) / Math.PI;
  if (H < 0) H += 360;
  return [L, C, H];
}

/** Linear sRGB component → gamma-corrected sRGB (0–1). */
function linearToGamma(c: number): number {
  return c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
}

/** Gamma-corrected sRGB (0–1) → linear sRGB component. */
function gammaToLinear(c: number): number {
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

/** Clamp a value to 0–255 integer range. */
function clamp255(v: number): number {
  return Math.max(0, Math.min(255, Math.round(v * 255)));
}

/** Convert OKLCH to hex (#rrggbb), clamping out-of-gamut values to sRGB bounds. */
function oklchToHex(L: number, C: number, H: number): string {
  const [lr, lg, lb] = oklchToLinearRGB(L, C, H);
  const r = clamp255(linearToGamma(lr));
  const g = clamp255(linearToGamma(lg));
  const b = clamp255(linearToGamma(lb));
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

/** Convert hex (#rrggbb) → OKLCH. Inverse of oklchToHex (lossy for clamped inputs). */
function hexToOklch(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  const r = gammaToLinear(parseInt(h.slice(0, 2), 16) / 255);
  const g = gammaToLinear(parseInt(h.slice(2, 4), 16) / 255);
  const b = gammaToLinear(parseInt(h.slice(4, 6), 16) / 255);
  return linearRGBToOklch(r, g, b);
}

// ── Golden angle ──────────────────────────────────────────────────────────

const GOLDEN_ANGLE = 137.50776405003785; // 360 * (1 - 1/φ)
const HUE_SEED = 9; // offset so index 0 starts near green (~158° in OKLCH)

// ── Range mapping per theme ───────────────────────────────────────────────
//
// Dark — neon phosphor, tuned against the near-black background. Bit-identical
// to the original pre-theme-aware palette.
//
// Light — wall-street ink, tuned for the warm cream background. Lightness
// drops from [0.68–0.85] to [0.40–0.55] (ink-on-parchment weight) and chroma
// from [0.14–0.22] to [0.08–0.16] (conservative saturation, matches the
// light-mode semantic tokens). Hue is preserved: a green layer stays green,
// just rendered as forest green instead of neon mint.

const DARK_L_MIN = 0.68;
const DARK_L_MAX = 0.85;
const DARK_C_MIN = 0.14;
const DARK_C_MAX = 0.22;

const LIGHT_L_MIN = 0.4;
const LIGHT_L_MAX = 0.55;
const LIGHT_C_MIN = 0.08;
const LIGHT_C_MAX = 0.16;

interface Range {
  lMin: number;
  lMax: number;
  cMin: number;
  cMax: number;
}

function rangeFor(theme: PaletteTheme): Range {
  return theme === "light"
    ? {
        lMin: LIGHT_L_MIN,
        lMax: LIGHT_L_MAX,
        cMin: LIGHT_C_MIN,
        cMax: LIGHT_C_MAX,
      }
    : {
        lMin: DARK_L_MIN,
        lMax: DARK_L_MAX,
        cMin: DARK_C_MIN,
        cMax: DARK_C_MAX,
      };
}

/**
 * Reproject OKLCH L/C from one theme's range into another's, preserving hue.
 *
 * Pure affine — no clamping at the normalization step — so reproject(A→B)
 * composed with reproject(B→A) is the algebraic identity. Round-trip drift
 * collapses to sRGB 8-bit quantization + gamut clipping inside oklchToHex,
 * which empirically keeps the median user-picked color round-trip at <2 RGB.
 * Inputs outside the source range extrapolate linearly, preserving relative
 * brightness/saturation ordering for user-custom hexes.
 */
function reproject(
  L: number,
  C: number,
  H: number,
  from: Range,
  to: Range,
): [number, number, number] {
  const lScale = (to.lMax - to.lMin) / (from.lMax - from.lMin);
  const cScale = (to.cMax - to.cMin) / (from.cMax - from.cMin);
  return [
    to.lMin + (L - from.lMin) * lScale,
    to.cMin + (C - from.cMin) * cScale,
    H,
  ];
}

// ── Public API ────────────────────────────────────────────────────────────

/**
 * Generate a perceptually distinct layer color for the given index in the target theme.
 * Default theme is dark — preserves call sites that haven't opted in to theme awareness
 * (new-layer defaults, tests, storage paths). Deterministic: same (index, theme) pair
 * always returns the same hex string.
 */
export function layerColor(
  index: number,
  theme: PaletteTheme = "dark",
): string {
  const hue = ((index + HUE_SEED) * GOLDEN_ANGLE) % 360;
  const lNorm = halton(index, 2);
  const cNorm = halton(index, 3);
  const { lMin, lMax, cMin, cMax } = rangeFor(theme);
  return oklchToHex(
    lMin + lNorm * (lMax - lMin),
    cMin + cNorm * (cMax - cMin),
    hue,
  );
}

/**
 * Transform a canonical (dark-mode) hex into the target theme's rendering.
 * Hue preserved; L/C are affinely remapped from the dark range onto the target
 * range. For palette-generated colors this closely matches a direct derivation
 * (layerColor(i, "light") ≈ displayLayerColor(layerColor(i), "light")). For
 * user-picked colors outside the dark range, the affine transform extrapolates,
 * preserving relative brightness and saturation ordering.
 *
 * Identity in dark mode.
 */
export function displayLayerColor(
  canonicalHex: string,
  theme: PaletteTheme,
): string {
  if (isCustomLayerColor(canonicalHex)) return CUSTOM_LAYER_SOLID_COLOR;
  if (!canonicalHex.startsWith("#")) return canonicalHex;
  if (theme === "dark") return canonicalHex;
  const [L, C, H] = hexToOklch(canonicalHex);
  const [L2, C2, H2] = reproject(L, C, H, rangeFor("dark"), rangeFor("light"));
  return oklchToHex(L2, C2, H2);
}

/**
 * Convert a picker-emitted hex (tuned for the current theme) back to the canonical
 * dark-mode form for storage. Exact algebraic inverse of displayLayerColor in
 * OKLCH — round-trip drift is bounded only by sRGB quantization and gamut
 * clipping, not by this math.
 *
 * Identity in dark mode.
 */
export function canonicalLayerColor(
  pickedHex: string,
  theme: PaletteTheme,
): string {
  if (pickedHex === CUSTOM_LAYER_COLOR || pickedHex === CUSTOM_LAYER_GRADIENT) {
    return CUSTOM_LAYER_COLOR;
  }
  if (!pickedHex.startsWith("#")) return pickedHex;
  if (theme === "dark") return pickedHex;
  const [L, C, H] = hexToOklch(pickedHex);
  const [L2, C2, H2] = reproject(L, C, H, rangeFor("light"), rangeFor("dark"));
  return oklchToHex(L2, C2, H2);
}

export const DEFAULT_LAYER_COLOR = layerColor(0);
